// Shrinks an oversized user image so it fits the upload cap, instead of rejecting it.
//
// WHY THIS EXISTS: camera photos routinely arrive as ~29 MP JPEGs saved at near-maximum quality —
// 23-32 MB for a single garment shot (Aydınlı, 2026-10-16). Every upload layer used to answer
// that with `validation:file.size` and nothing else. Re-encoding the same pixels at q0.92 brings
// those files to 14-17 MB with no visible loss, and the backend downscales provider inputs to
// 3072 px anyway (`refabric-v3-api` `inference/adapters/input_file_cap.py`), so nothing a model
// reads is lost by doing it here. The product owner decided the optimized copy IS the stored
// asset — no original is kept.
//
// Policy, in order:
//   1. At or under `maxBytes`, or not jpeg/png/webp → returned untouched (same File object).
//   2. Over `UPLOAD_HARD_MAX_BYTES` → `too_large`, without decoding anything.
//   3. Otherwise decode once (EXIF orientation applied by the browser) and try, in order,
//      full resolution → 6144 px long edge → 4096 px long edge; the first encode that fits wins.
//      A candidate whose canvas cannot be created or encoded falls through to the next one —
//      that is how iOS Safari's ~16.7 MP canvas ceiling is handled, without device sniffing.
//   4. Opaque output is JPEG q0.92; a png/webp with real transparency stays PNG.
//   5. Any failure → `failed`. The CALLER then applies its normal size rejection; an oversized
//      file is never uploaded as a fallback.
//
// Pure: no React, no snackbar. The notice belongs to `hooks/useUploadImagePreparation`.

export const UPLOAD_HARD_MAX_MB = 100;
export const UPLOAD_HARD_MAX_BYTES = UPLOAD_HARD_MAX_MB * 1024 * 1024;
export const OPTIMIZE_LONG_EDGES = [6144, 4096];
export const OPTIMIZE_JPEG_QUALITY = 0.92;
export const OPTIMIZABLE_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

// Rows scanned per `getImageData` call when looking for transparency. A full-frame read of a
// 29 MP canvas is a ~116 MB copy; strips keep the peak bounded.
const ALPHA_SCAN_STRIP_ROWS = 256;

export type ImageOptimizationStatus = "untouched" | "optimized" | "too_large" | "failed";

export interface ImageOptimizationResult {
  file: File;
  status: ImageOptimizationStatus;
}

interface DecodedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

// Same two decode paths as `exifOrientation.decodeOriented`: both rely on the browser applying the
// EXIF orientation itself, so the pixels drawn below are already upright and the re-encoded file
// needs no tag.
const decode = async (file: File): Promise<DecodedImage> => {
  if (typeof createImageBitmap === "function") {
    try {
      // "from-image" postdates TS 4.6's DOM lib (its ImageOrientation union lacks the value), hence the cast
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" as any });

      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
    } catch {
      // older Safari rejects the options bag — fall through to <img> decode
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Failed to decode image for upload optimization"));
      img.src = url;
    });

    return { source: image, width: image.naturalWidth, height: image.naturalHeight, release: () => URL.revokeObjectURL(url) };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
};

// Full resolution first, then each long-edge step that is actually SMALLER than the step before.
// Never upscales; the aspect ratio is kept exactly (rounded to whole pixels, at least 1).
export const optimizationCandidates = (width: number, height: number): { width: number; height: number }[] => {
  const candidates = [{ width, height }];
  let previousLongEdge = Math.max(width, height);

  for (const longEdge of OPTIMIZE_LONG_EDGES) {
    if (longEdge >= previousLongEdge) continue;
    const scale = longEdge / Math.max(width, height);
    candidates.push({ width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) });
    previousLongEdge = longEdge;
  }

  return candidates;
};

const hasTransparency = (context: CanvasRenderingContext2D, width: number, height: number): boolean => {
  for (let top = 0; top < height; top += ALPHA_SCAN_STRIP_ROWS) {
    const rows = Math.min(ALPHA_SCAN_STRIP_ROWS, height - top);
    const { data } = context.getImageData(0, top, width, rows);
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) return true;
    }
  }

  return false;
};

const toBlob = (canvas: HTMLCanvasElement, type: string, quality?: number) =>
  new Promise<Blob | null>((resolve) => {
    try {
      canvas.toBlob(resolve, type, quality);
    } catch {
      resolve(null);
    }
  });

// Frees the backing store now instead of whenever GC gets to it — at these sizes a lingering
// canvas is tens of MB, and a batch would otherwise stack them.
const releaseCanvas = (canvas: HTMLCanvasElement) => {
  canvas.width = 0;
  canvas.height = 0;
};

const renamedFor = (file: File, type: string) => {
  const extension = type === "image/png" ? "png" : "jpg";
  const dot = file.name.lastIndexOf(".");
  const base = dot > 0 ? file.name.slice(0, dot) : file.name;

  return `${base}.${extension}`;
};

const optimize = async (file: File, maxBytes: number): Promise<ImageOptimizationResult> => {
  let decoded: DecodedImage | null = null;

  try {
    decoded = await decode(file);
    const { source, width, height } = decoded;
    // Only png/webp can carry alpha; it is decided once, on the first canvas that draws.
    let outputType: string | null = file.type === "image/jpeg" ? "image/jpeg" : null;

    for (const candidate of optimizationCandidates(width, height)) {
      let canvas: HTMLCanvasElement | null = null;

      try {
        // Creation and sizing are inside the guard too: a browser may throw on the allocation itself.
        canvas = document.createElement("canvas");
        canvas.width = candidate.width;
        canvas.height = candidate.height;
        const context = canvas.getContext("2d");
        if (!context) continue;
        context.drawImage(source, 0, 0, candidate.width, candidate.height);

        if (!outputType) outputType = hasTransparency(context, candidate.width, candidate.height) ? "image/png" : "image/jpeg";

        const blob = await toBlob(canvas, outputType, outputType === "image/jpeg" ? OPTIMIZE_JPEG_QUALITY : undefined);
        // A browser that cannot encode the requested type silently returns PNG — never ship a
        // body whose bytes disagree with the content type it will be signed with.
        if (!blob || blob.type !== outputType || blob.size > maxBytes) continue;

        return { file: new File([blob], renamedFor(file, outputType), { type: outputType, lastModified: file.lastModified }), status: "optimized" };
      } catch {
        // canvas too large for this device, tainted, or out of memory — try the next, smaller one
      } finally {
        if (canvas) releaseCanvas(canvas);
      }
    }

    return { file, status: "failed" };
  } catch {
    return { file, status: "failed" };
  } finally {
    decoded?.release();
  }
};

// One optimization at a time, app-wide. Each holds a decoded bitmap plus a canvas — hundreds of MB
// for a 29 MP photo — so independent batches (two upload boxes, validator + uploader) must queue
// behind each other rather than each running their own bounded pool.
let queueTail: Promise<unknown> = Promise.resolve();
const enqueue = <T>(task: () => Promise<T>): Promise<T> => {
  const run = queueTail.then(task, task);
  queueTail = run.catch(() => undefined);

  return run;
};

// The validator and the uploader may both see the same original; the second one reuses the first
// one's result instead of encoding again. Keyed by the File object and the target, weakly.
const cache = new WeakMap<File, Map<number, Promise<ImageOptimizationResult>>>();

export const needsImageOptimization = (file: File, maxBytes: number): boolean => OPTIMIZABLE_IMAGE_TYPES.includes(file.type) && file.size > maxBytes;

export const optimizeImageForUpload = (file: File, maxBytes: number): Promise<ImageOptimizationResult> => {
  if (!needsImageOptimization(file, maxBytes)) return Promise.resolve({ file, status: "untouched" });
  if (file.size > UPLOAD_HARD_MAX_BYTES) return Promise.resolve({ file, status: "too_large" });

  let byTarget = cache.get(file);
  if (!byTarget) {
    byTarget = new Map();
    cache.set(file, byTarget);
  }
  const cached = byTarget.get(maxBytes);
  if (cached) return cached;

  const pending = enqueue(() => optimize(file, maxBytes));
  byTarget.set(maxBytes, pending);

  return pending;
};
